import { memo, useState, useCallback, useMemo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import MediaLightbox from '../MediaLightbox';
import GenerationStrip from './GenerationStrip';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { HANDLES, type EditNodeData, type GenerateNodeData, type GenerationEntry, useCanvasStore } from '../useCanvasStore';
import { studioService } from '../../../../services/studio.service';
import { useStudioStore } from '../../stores/studio.store';
import { getAssetSrc } from '../../utils/assetUrl';
import { useNodeTextField } from './useNodeTextField';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';

function EditImageNode({ id, data }: NodeProps & { data: EditNodeData }) {
  const updateNodeData = useCanvasStore((s) => s.updateNodeData);
  const removeNode = useCanvasStore((s) => s.removeNode);
  const edges = useCanvasStore((s) => s.edges); // subscribe to edge changes
  const nodes = useCanvasStore((s) => s.nodes); // subscribe to node changes
  const addAsset = useStudioStore((s) => s.addAsset);
  const projectId = useStudioStore((s) => s.projectId);
  const { enqueueSnackbar } = useSnackbar();
  const [lightbox, setLightbox] = useState(false);
  const syncInstruction = useMemo(() => (v: string) => updateNodeData(id, { instruction: v }), [id, updateNodeData]);
  const instructionField = useNodeTextField(data.instruction, syncInstruction);

  // Reactive: re-compute when edges/nodes change
  const sourceEdge = edges.find((e) => e.target === id && e.targetHandle === HANDLES.editSource);
  const sourceNode = sourceEdge ? nodes.find((n) => n.id === sourceEdge.source) : undefined;
  const sourceImage = (sourceNode?.data as GenerateNodeData)?.resultImage;

  const handleSelectGeneration = useCallback((idx: number) => {
    const gens = data.generations || [];
    const gen = gens[idx];
    if (!gen) return;
    updateNodeData(id, {
      selectedGeneration: idx,
      resultImage: gen.image,
      resultMimeType: gen.mimeType,
      assetId: gen.assetId,
      gcsPath: gen.gcsPath,
      ...(gen.prompt !== undefined && { instruction: gen.prompt }),
    });
  }, [id, data.generations, updateNodeData]);

  const handleEdit = useCallback(async () => {
    if (!data.instruction.trim() || !sourceImage) return;
    updateNodeData(id, { isGenerating: true });

    try {
      const resp = await fetch(getAssetSrc(sourceImage, 'image/png'));
      const blob = await resp.blob();
      const file = new File([blob], 'source.png', { type: 'image/png' });

      const res = await studioService.edit(file, data.instruction, projectId || undefined);
      if (res.image) {
        const newGen: GenerationEntry = {
          image: res.image,
          mimeType: res.mimeType,
          assetId: res.assetId,
          gcsPath: res.gcsPath,
          prompt: data.instruction,
          timestamp: Date.now(),
        };
        const prevGens = data.generations || [];
        const newGens = [...prevGens, newGen];
        const newIdx = newGens.length - 1;
        updateNodeData(id, {
          resultImage: res.image,
          resultMimeType: res.mimeType,
          assetId: res.assetId,
          gcsPath: res.gcsPath,
          isGenerating: false,
          generations: newGens,
          selectedGeneration: newIdx,
        });
        addAsset({
          type: 'image',
          data: res.image,
          mimeType: res.mimeType,
          prompt: data.instruction,
          tool: 'edit',
          serverId: res.assetId,
        });
      }
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
      updateNodeData(id, { isGenerating: false });
    }
  }, [id, data.instruction, sourceImage, projectId, updateNodeData, addAsset, enqueueSnackbar]);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        width: 280,
        overflow: 'hidden',
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id={HANDLES.editSource}
        style={{ background: '#26a69a', width: 10, height: 10 }}
        title="Source Image"
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          bgcolor: '#00695c',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Edit sx={{ fontSize: 16 }} />
          <Typography variant="caption" fontWeight={600}>
            Edit Image
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => removeNode(id)} sx={{ color: 'inherit' }}>
          <Delete sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Chip
          size="small"
          label={sourceImage ? '✓ Source connected' : '○ Connect source image'}
          color={sourceImage ? 'success' : 'default'}
          variant={sourceImage ? 'filled' : 'outlined'}
          sx={{ fontSize: '0.7rem' }}
        />

        <TextField
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          placeholder="e.g. Change the background to a sunset"
          value={instructionField.value}
          onChange={instructionField.onChange}
          onFocus={instructionField.onFocus}
          onBlur={instructionField.onBlur}
          fullWidth
          className="nodrag nowheel"
        />

        <Button
          variant="contained"
          size="small"
          onClick={handleEdit}
          disabled={data.isGenerating || !sourceImage || !data.instruction.trim()}
          startIcon={data.isGenerating ? <CircularProgress size={14} /> : <Edit sx={{ fontSize: 14 }} />}
          fullWidth
          color="success"
        >
          {data.isGenerating ? 'Editing…' : 'Edit'}
        </Button>

        {/* Result preview */}
        {data.resultImage && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1/1',
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <img
              src={getAssetSrc(data.resultImage, data.resultMimeType || 'image/png')}
              alt="Edited"
              style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
              onClick={() => setLightbox(true)}
            />
            <SaveToLibraryButton assetId={data.assetId} defaultName={data.instruction} />
          </Box>
        )}
        {data.resultImage && (
          <MediaLightbox
            open={lightbox}
            onClose={() => setLightbox(false)}
            src={getAssetSrc(data.resultImage, data.resultMimeType || 'image/png')}
            type="image"
          />
        )}

        {/* Generation history strip */}
        {data.generations && data.generations.length > 1 && (
          <GenerationStrip
            generations={data.generations}
            selectedIndex={data.selectedGeneration ?? data.generations.length - 1}
            onSelect={handleSelectGeneration}
            type="image"
          />
        )}
      </Box>

      {/* Output handle */}
      {data.resultImage && (
        <Handle
          type="source"
          position={Position.Right}
          id={HANDLES.imageOut}
          style={{ background: '#90caf9', width: 10, height: 10 }}
        />
      )}
    </Box>
  );
}

export default memo(EditImageNode);
